import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import size from 'lodash/size';
import isArray from 'lodash/isArray';
import { QUERY_KEYS } from '../../config/constants';
import {
  addUserFn,
  deleteUserByIdFn,
  downloadLogsFn,
  editUserByIdFn,
  getAllOfferReportingsFn,
  getBookingListFn,
  getBookingStatisticsFn,
  getOfferReportingListFn,
  getUserFormFn,
  getUsersListFn,
  impersonateUserFn,
  updateUserByIdFn,
  uploadUserFn,
} from '../../transactions/user/admin';
import { downloadInvoicesByIdFn } from '../../transactions/download/download';
import {
  getOfferStatusFn,
  getOffersFn,
  offerApproveReject,
} from '../../transactions/offer/offer';
import { queryClient } from '../../utils/queryClient';
import { toast } from '../../components/ui/Toast/useToast';
const {
  USER_LIST,
  BOOKING_REPORTINGS,
  BOOKING_STATISTICS,
  OFFER_REPORTING,
  DOWNLOAD_LOGS,
  OFFER_LIST,
  OFFER_DETAILS,
  DOWNLOAD_LOGS_DOCUMENT,
  IMPERSONATE_USER,
  EDIT_USER_FORM_FIELDS,
  ADD_USER_FORM_FILEDS,
} = QUERY_KEYS;

export const useGetUerList = ({ length, orderType, orderBy, search, page }) => {
  return useInfiniteQuery({
    queryKey: [
      USER_LIST,
      {
        length,
        orderType,
        orderBy,
        search,
        page,
      },
    ],
    queryFn: getUsersListFn,
    getNextPageParam: (lastPage, pages) => {
      return pages.length + 1;
    },
    staleTime: Infinity,
    cacheTime: Infinity,
  });
};

export const useGetBookingList = ({
  length,
  orderType,
  orderBy,
  search,
  page,
}) => {
  return useInfiniteQuery({
    queryKey: [
      BOOKING_REPORTINGS,
      {
        length,
        orderType,
        orderBy,
        search,
        page,
      },
    ],
    queryFn: getBookingListFn,
    getNextPageParam: (lastPage, pages) => {
      return pages.length + 1;
    },
    staleTime: Infinity,
    cacheTime: Infinity,
  });
};

export const useGetOffreReportingList = () => {
  return useQuery({
    queryKey: [OFFER_REPORTING],
    queryFn: getAllOfferReportingsFn,
    staleTime: Infinity,
    cacheTime: Infinity,
  });
};

export const useGetOfferReportings = ({
  length,
  orderType,
  orderBy,
  search,
  page,
}) => {
  return useInfiniteQuery({
    queryKey: [
      OFFER_REPORTING,
      {
        length,
        orderType,
        orderBy,
        search,
        page,
      },
    ],
    queryFn: getOfferReportingListFn,
    getNextPageParam: (lastPage, pages) => {
      return pages.length + 1;
    },
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: search !== undefined
  });
};

export const useDownloadLogs = (
  { length, orderType, orderBy, search, page },
  dateRange
) => {
  return useInfiniteQuery({
    queryKey: [
      DOWNLOAD_LOGS,
      {
        length,
        orderType,
        orderBy,
        search,
        page,
      },
      dateRange,
    ],
    queryFn: downloadLogsFn,
    getNextPageParam: (lastPage, pages) => {
      return pages.length + 1;
    },
    staleTime: Infinity,
    cacheTime: Infinity,
  });
};

export const useDownloadLogsDocument = (docId) => {
  return useQuery({
    queryKey: [DOWNLOAD_LOGS_DOCUMENT, docId],
    queryFn: () => downloadInvoicesByIdFn(docId),
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: !!docId,
  });
};

export const useImpersonateUser = (impersonateUserData) => {
  const navigate = useNavigate();

  return useQuery({
    queryKey: [IMPERSONATE_USER, impersonateUserData],
    queryFn: impersonateUserFn,
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: !!impersonateUserData.id,
    onSuccess: (response) => {
      if (response && response.exceptions) {
        if (size(response.exceptions) > 0) {
          const exceptionsArray = isArray(response.exceptions);
          const message = exceptionsArray
            ? response.exceptions.slice(-1)
            : 'Παρουσιάστηκε κάποιο πρόβλημα';
          toast({
            variant: 'destructive',
            description: message || 'Παρουσιάστηκε κάποιο πρόβλημα',
          });
        }
      }
      if (response && response.data?.access_token) {
        const jwt = response?.data?.access_token;
        const adminJWT = localStorage.getItem('jwt');

        if (jwt && adminJWT) {
          navigate(`/impersonate?jwt=${jwt}`, {
            state: { adminJWT },
          });
        }
      }
    },
    onError: () => {
      toast({
        variant: 'destructive',
        description: 'Παρουσιάστηκε κάποιο πρόβλημα',
      });
    },
  });
};

export const useGetUserForm = (userId) => {
  return useQuery({
    queryKey: [ADD_USER_FORM_FILEDS],
    queryFn: getUserFormFn,
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: !userId, // call api when no user id presrnt
  });
};

export const useEditUserForm = (userId) => {
  return useQuery({
    queryKey: [EDIT_USER_FORM_FIELDS, userId],
    queryFn: () => editUserByIdFn(userId),
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: !!userId,
  });
};

export const useAddUser = () => {
  return useMutation({
    mutationFn: (payload) => addUserFn(payload),
  });
};

export const useUpdateUser = () => {
  return useMutation({
    mutationFn: ({ userId, payload }) => updateUserByIdFn(userId, payload),
  });
};

export const useDeleteUser = () => {
  return useMutation({
    mutationFn: (userId) => deleteUserByIdFn(userId),
  });
};

export const useUploadUsers = () => {
  return useMutation({
    mutationFn: (payload) => uploadUserFn(payload),
  });
};

export const useGetBookingStatistics = () => {
  return useQuery({
    queryKey: [BOOKING_STATISTICS],
    queryFn: getBookingStatisticsFn,
    staleTime: Infinity,
    cacheTime: Infinity,
  });
};

export const useGetOfferCheckList = () => {
  return useQuery({
    queryKey: [OFFER_LIST],
    queryFn: getOffersFn,
    staleTime: Infinity,
    cacheTime: Infinity,
  });
}; // getOfferStatusFn

export const useGetOfferDetails = (cncode) => {
  return useQuery({
    queryKey: [OFFER_DETAILS, cncode],
    queryFn: () => getOfferStatusFn(cncode),
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: !!cncode,
  });
};

export const usePostOffer = () => {
  return useMutation({
    mutationFn: ({ url, payload }) => offerApproveReject(url, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [OFFER_LIST] });
    },
  });
};
